//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import * as yup from 'yup'
import { InferType } from 'yup'
import { ERC20Wei } from './conversions'
import { yupErc20Wei } from './Enums'
import {
  PoolBaseAccountCTO,
  PoolBaseAccountCTOSchemaValues,
  PoolBaseAccountLenderFlexCTOSchemaValues,
  PoolBaseAccountSummaryCTOSchemaValues,
} from './PoolBaseAccountCTO'
import { StringTypeEnum, toFieldMetaData } from './SchemaTypes'
import { convertValuesToDecStr, convertYupSchemaValuesToDecStr } from './schemaUtils'
import { PoolDynamicAccountCTO, PoolDynamicAccountCTOSchema, PoolDynamicAccountTTO } from './PoolDynamicAccountCTO'

export const PoolFlexAccountOnlyCTOSchemaValues: Record<string, yup.Schema> = {
  dailyInterestRate: yupErc20Wei
    .label('Single Day Interest Rate')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'dailyInterestRate',
        description: 'Interest rate earned in the day',
        stringType: StringTypeEnum.SINGLE_DAY_APR,
      })
    ),
  maxWithdrawRequest: yupErc20Wei.label('Max Withdrawal Request').meta(
    toFieldMetaData({
      propName: 'maxWithdrawRequest',
      description: 'The maximum amount of assets you can request to be withdrawn.',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  maxRedeemRequest: yupErc20Wei.label('Max Tokens Redeemable').meta(
    toFieldMetaData({
      propName: 'maxRedeemRequest',
      description:
        'The maximum amount that can be requested for withdrawal by the account, denominated in Vault Tokens. ',
      stringType: StringTypeEnum.PPT_SHARES,
    })
  ),
  requestedSharesOf: yupErc20Wei.label('Outstanding Withdrawal Requests (Tokens)').meta(
    toFieldMetaData({
      propName: 'requestedSharesOf',
      description: 'The amount of outstanding withdrawal requests for the account, denominated in Vault Tokens. ',
      stringType: StringTypeEnum.PPT_SHARES,
    })
  ),
  requestedAssetsOf: yupErc20Wei.label('Outstanding Withdrawal Requests (Assets)').meta(
    toFieldMetaData({
      propName: 'requestedAssetsOf',
      description: 'The amount of withdrawal request currently pending repayment.',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),

  principalEarningInterest: yupErc20Wei.label('Principal Earning Interest').meta(
    toFieldMetaData({
      propName: 'principalEarningInterest',
      description: 'Your current amount of loan principal earning the Loan Fee Rate.',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),

  interestAccrued: yupErc20Wei.label('Interest Accrued').meta(
    toFieldMetaData({
      propName: 'interestAccrued',
      description: 'The amount of interest you have accrued to date.',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  assetsDeposited: yupErc20Wei.label('Assets Loaned').meta(
    toFieldMetaData({
      propName: 'assetsDeposited',
      description: 'The amount of assets you have loaned to date.',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  assetsWithdrawn: yupErc20Wei.label('Assets Withdrawn').meta(
    toFieldMetaData({
      propName: 'assetsWithdrawn',
      description: 'The amount of your loans that have been repaid to date.',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  sharesTransitioningIn: yupErc20Wei.label('Loans Pending (Tokens)').meta(
    toFieldMetaData({
      propName: 'sharesTransitioningIn',
      description:
        'The amount of loans which are currently pending for the account, denominated in vault tokens. The tokens will begin accruing interest after the transferInDays lapse. ',
      stringType: StringTypeEnum.PPT_SHARES,
    })
  ),
  assetsTransitioningIn: yupErc20Wei.label('Loans Pending (Assets)').meta(
    toFieldMetaData({
      propName: 'assetsTransitioningIn',
      description:
        'The amount of the loans which are currently pending. The assets will begin accruing interest after the Advance Processing Time lapses.',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),

  assetsDueForWithdraws: yupErc20Wei.label('Withdrawal Requests Due').meta(
    toFieldMetaData({
      propName: 'assetsDueForWithdraws',
      description: 'The amount of assets currently due to be repaid.',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),

  accountOutstandingLoanPrincipals: yupErc20Wei.label('Outstanding Loan Principals').meta(
    toFieldMetaData({
      propName: 'accountOutstandingLoanPrincipals',
      description: 'PrincipalEarningInterest +AssetsTransitioningIn + AssetsDueForWithdraws',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
  interestInDay: yupErc20Wei.label('Interest on Day').meta(
    toFieldMetaData({
      propName: 'interestInDay',
      description: 'Interest earned in the day',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
}

export const PoolFlexAccountSummaryCTOSchema = yup
  .object({
    ...PoolBaseAccountSummaryCTOSchemaValues,
  })
  .label('PoolFlexAccountSummaryCTO')
  .meta(
    toFieldMetaData({
      propName: 'PoolFlexAccountSummaryCTO',
      map: 'PoolFlexAccountCTO',
      description: 'Pool Flex Account CTO',
    })
  )
export const PoolFlexAccountCTOSchema = yup
  .object({
    ...PoolBaseAccountCTOSchemaValues,
    ...PoolFlexAccountOnlyCTOSchemaValues,
  })
  .label('PoolFlexAccountCTO')
  .meta(toFieldMetaData({ propName: 'PoolFlexAccountCTO', description: 'Pool Account CTO' }))

export const PoolFlexAccountOnlyCTOSchema = yup.object({
  ...PoolFlexAccountOnlyCTOSchemaValues,
})

export interface PoolFlexAccountSummaryCTO extends InferType<typeof PoolFlexAccountSummaryCTOSchema> {}
export interface PoolFlexAccountCTO extends PoolBaseAccountCTO, InferType<typeof PoolFlexAccountOnlyCTOSchema> {
  dailyInterestRate: string
  maxWithdrawRequest: ERC20Wei
  maxRedeemRequest: ERC20Wei
  requestedAssetsOf: ERC20Wei
  requestedSharesOf: ERC20Wei
  principalEarningInterest: ERC20Wei
  interestAccrued: ERC20Wei
  interestInDay: ERC20Wei
  assetsDeposited: ERC20Wei
  assetsWithdrawn: ERC20Wei
  sharesTransitioningIn: ERC20Wei
  assetsTransitioningIn: ERC20Wei
  assetsDueForWithdraws: ERC20Wei
  accountOutstandingLoanPrincipals: ERC20Wei
}

export const PoolFlexAccountTTOOnlySchema = yup
  .object({
    liquidityTokenSymbol: yup
      .string()
      .label('liquidityTokenSymbol')
      .meta(toFieldMetaData({ propName: 'liquidityTokenSymbol', stringType: StringTypeEnum.SYMBOL })),
  })
  .concat(yup.object(convertYupSchemaValuesToDecStr(PoolFlexAccountCTOSchema)))
  .concat(PoolFlexAccountCTOSchema)

export function PoolLenderReportingAccountFlexCTOSchema(properties?: string[]): yup.ObjectSchema<any> {
  if (!properties) {
    return yup.object({
      ...PoolBaseAccountLenderFlexCTOSchemaValues,
      ...PoolFlexAccountOnlyCTOSchemaValues,
    })
  } else {
    const schemaValues: Record<string, yup.Schema> = {}
    properties.forEach((prop) => {
      if (PoolBaseAccountLenderFlexCTOSchemaValues[prop]) {
        schemaValues[prop] = PoolBaseAccountLenderFlexCTOSchemaValues[prop]
      }
      if (PoolFlexAccountOnlyCTOSchemaValues[prop]) {
        schemaValues[prop] = PoolFlexAccountOnlyCTOSchemaValues[prop]
      }
      // if (PoolAccountOnlyCTOSchemaValues[prop]) {
      //   schemaValues[prop] = PoolAccountOnlyCTOSchemaValues[prop]
      // }
      if (!schemaValues[prop]) {
        throw new Error(`Property ${prop} not found in PoolReportingAccountCTOSchema`)
      }
    })

    return yup.object(schemaValues)
  }
}

export interface PoolFlexAccountTTO extends PoolFlexAccountCTO, InferType<typeof PoolFlexAccountTTOOnlySchema> {}

export function toPoolFlexAccountTTO(poolFlexAccountCTO: PoolFlexAccountCTO): PoolFlexAccountTTO {
  return {
    ...poolFlexAccountCTO,

    ...convertValuesToDecStr(PoolFlexAccountCTOSchema, poolFlexAccountCTO),
  }
}
export function toPoolDynamicAccountTTO(poolDynamicAccountCTO: PoolDynamicAccountCTO): PoolDynamicAccountTTO {
  return {
    ...poolDynamicAccountCTO,

    ...convertValuesToDecStr(PoolDynamicAccountCTOSchema, poolDynamicAccountCTO),
  }
}
